import React, { useEffect, useState } from "react";
import axios from "axios";
import { logHistory } from "../../utils/historyLogger";

function FoodCommittee() {
  const [foods, setFoods] = useState([]);
  const role = localStorage.getItem("role");
  const canEdit = role === "admin" || role === "superadmin";
  const canDelete = role === "superadmin";
  const canAdd =
    role === "user" || role === "admin" || role === "superadmin";

  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    date: "",
    request: "",
    requestedPerson: "",
    responsiblePerson: "",
    solution: "",
    remark: "",
    status: "Pending",
  });

  useEffect(() => {
    fetchFoods();
  }, []);

  const fetchFoods = async () => {
    try {
      const res = await axios.get("https://hrpsc.swisstekaluminium.com/api/foods");
      setFoods(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
      alert("Failed to fetch data");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const clearForm = () => {
    setFormData({
      date: "",
      request: "",
      requestedPerson: "",
      responsiblePerson: "",
      solution: "",
      remark: "",
      status: "Pending",
    });

    setEditingId(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.request || !formData.requestedPerson) {
        alert("Please fill required fields");
        return;
      }

      if (editingId) {
        await axios.put(
          `https://hrpsc.swisstekaluminium.com/api/foods/${editingId}`,
          formData
        );

        await logHistory(
          "Food Committee",
          "Updated Food Record",
          "Food record updated"
        );

        alert("Updated Successfully");
      } else {
        await axios.post(
          "https://hrpsc.swisstekaluminium.com/api/foods",
          formData
        );

        await logHistory(
          "Food Committee",
          "Added Food Record",
          "Food record added"
        );

        alert("Added Successfully");
      }

      await fetchFoods();
      clearForm();
    } catch (err) {
      console.log(err);
      alert("Error");
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure to delete?"
    );

    if (!confirmDelete) return;

    try {
      await axios.delete(
        `https://hrpsc.swisstekaluminium.com/api/foods/${id}`
      );

      await logHistory(
        "Food Committee",
        "Deleted Food Record",
        "Food record deleted"
      );

      fetchFoods();
    } catch (err) {
      console.log(err);
      alert("Delete Failed");
    }
  };

  // ✅ FIXED (_id → id)
  const handleEdit = (item) => {
    setEditingId(item.id);

    setFormData({
      date: item.date ? item.date.substring(0, 10) : "",
      request: item.request || "",
      requestedPerson: item.requestedPerson || "",
      responsiblePerson: item.responsiblePerson || "",
      solution: item.solution || "",
      remark: item.remark || "",
      status: item.status || "Pending",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const toggleStatus = async (id, currentStatus) => {
    try {
      const updatedStatus =
        currentStatus === "Pending"
          ? "Completed"
          : "Pending";

      await axios.put(
        `https://hrpsc.swisstekaluminium.com/api/foods/${id}`,
        {
          status: updatedStatus,
        }
      );

      await logHistory(
        "Food Committee",
        "Updated Food Status",
        `Food record status changed to ${updatedStatus}`
      );

      fetchFoods();
    } catch (err) {
      console.log(err);
      alert("Status Update Failed");
    }
  };

  const downloadCSVReport = () => {
    if (foods.length === 0) {
      alert("No food committee data to download");
      return;
    }

    const headers = Object.keys(foods[0]);

    const rows = foods.map((item) =>
      headers
        .map(
          (field) =>
            `"${String(item[field] ?? "").replace(
              /"/g,
              '""'
            )}"`
        )
        .join(",")
    );

    const csvContent = [
      headers.join(","),
      ...rows,
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "food_committee_report.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const filteredFoods = foods.filter((item) => {
    const text = search.toLowerCase();

    return (
      item.request?.toLowerCase().includes(text) ||
      item.requestedPerson
        ?.toLowerCase()
        .includes(text) ||
      item.responsiblePerson
        ?.toLowerCase()
        .includes(text)
    );
  });

  return (
    <div className="page-container">
      <h1 className="page-title">
        Food Committee
      </h1>

      <div className="cards">
        <div className="card">
          <h3>Total Requests</h3>
          <p>{foods.length}</p>
        </div>

        <div className="card">
          <h3>Completed</h3>
          <p>
            {
              foods.filter(
                (item) =>
                  item.status === "Completed"
              ).length
            }
          </p>
        </div>

        <div className="card">
          <h3>Pending</h3>
          <p>
            {
              foods.filter(
                (item) =>
                  item.status === "Pending"
              ).length
            }
          </p>
        </div>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search by Request or Name..."
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <button
          className="clear-btn"
          onClick={() => setSearch("")}
        >
          Clear
        </button>

        <button
          className="primary-btn"
          onClick={downloadCSVReport}
        >
          Download Report
        </button>
      </div>

      {(canAdd || editingId) && (
        <>
          <div className="form-grid">
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />

            <input
              type="text"
              name="request"
              placeholder="Request"
              value={formData.request}
              onChange={handleChange}
            />

            <input
              type="text"
              name="requestedPerson"
              placeholder="Requested Person"
              value={formData.requestedPerson}
              onChange={handleChange}
            />

            <input
              type="text"
              name="responsiblePerson"
              placeholder="Responsible Person"
              value={formData.responsiblePerson}
              onChange={handleChange}
            />

            <input
              type="text"
              name="solution"
              placeholder="Solution"
              value={formData.solution}
              onChange={handleChange}
            />

            <input
              type="text"
              name="remark"
              placeholder="Remark"
              value={formData.remark}
              onChange={handleChange}
            />
          </div>

          <div className="button-group">
            <button
              className="primary-btn"
              onClick={handleSubmit}
            >
              {editingId
                ? "Update Request"
                : "Add Request"}
            </button>

            <button
              className="clear-btn"
              onClick={clearForm}
            >
              Clear Form
            </button>
          </div>
        </>
      )}
            <div className="table-container">
        <table className="custom-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Request</th>
              <th>Requested Person</th>
              <th>Responsible Person</th>
              <th>Solution</th>
              <th>Remark</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredFoods.length > 0 ? (
              filteredFoods.map((item) => (
                <tr key={item.id}>
                  <td>
                    {item.date
                      ? item.date.substring(0, 10)
                      : ""}
                  </td>

                  <td>{item.request}</td>

                  <td>{item.requestedPerson}</td>

                  <td>{item.responsiblePerson}</td>

                  <td>{item.solution}</td>

                  <td>{item.remark}</td>

                  <td>
                    {canEdit ? (
                      <button
                        className={
                          item.status === "Completed"
                            ? "completed-status"
                            : "pending-status"
                        }
                        onClick={() =>
                          toggleStatus(item.id, item.status)
                        }
                      >
                        {item.status}
                      </button>
                    ) : (
                      <span
                        className={
                          item.status === "Completed"
                            ? "completed-status"
                            : "pending-status"
                        }
                      >
                        {item.status}
                      </span>
                    )}
                  </td>

                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(item)}
                        >
                          Edit
                        </button>
                      )}

                      {canDelete && (
                        <button
                          className="delete-btn"
                          onClick={() =>
                            handleDelete(item.id)
                          }
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "gray",
                  }}
                >
                  No Records Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FoodCommittee;